import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, loadUser } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, CalendarDays, Trophy, ArrowRight, Users } from "lucide-react";

export function HackathonsList() {
  const [rows, setRows] = useState([]);
  useEffect(()=>{ api.get("/hackathons").then(r=>setRows(r.data)); },[]);
  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader/>
      <section className="bg-[#0A2540] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-[#F59E0B]">Hackathons</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold mt-3">Build. Compete. Get hired.</h1>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {rows.length === 0 ? (
          <Card className="p-16 text-center border-dashed border-slate-300">
            <Trophy className="w-10 h-10 mx-auto text-slate-300"/>
            <p className="mt-3 text-slate-500">No hackathons currently open.</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {rows.map(h => (
              <Link key={h.id} to={`/hackathons/${h.id}`} data-testid={`hack-card-${h.id}`} className="group">
                <Card className="overflow-hidden card-hover border-slate-200 h-full">
                  <div className="relative aspect-video overflow-hidden">
                    <img src={h.banner_url || "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1200&q=85"} alt={h.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                    {h.featured && <Badge className="absolute top-3 left-3 bg-[#F59E0B] text-[#0A2540] border-none hover:bg-[#F59E0B]">Featured</Badge>}
                  </div>
                  <div className="p-5">
                    <Badge variant="secondary" className="text-[10px]">{h.theme || "Hackathon"}</Badge>
                    <h3 className="font-display text-lg font-bold text-[#0A2540] mt-2">{h.name}</h3>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">{h.short_description}</p>
                    <div className="mt-3 text-xs text-slate-500 flex flex-wrap gap-3">
                      {h.event_start && <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3"/>{h.event_start}</span>}
                      <span>{h.mode}</span>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>
      <PublicFooter/>
    </div>
  );
}

export function HackathonDetail() {
  const { id } = useParams();
  const [h, setH] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState(false);
  const user = loadUser();
  useEffect(()=>{
    api.get(`/hackathons/${id}`).then(r=>setH(r.data)).catch(()=>{});
    if (user?.role === "candidate") {
      api.get("/candidate/hackathons").then(r=>setRegistered((r.data||[]).some(x=>x.hackathon_id===id))).catch(()=>{});
    }
  },[id, user?.role]);
  const register = async () => {
    if (!user) { window.location.href = "/login?next=/hackathons/" + id; return; }
    setRegistering(true);
    try {
      const { data } = await api.post(`/hackathons/${id}/register`, {});
      toast.success(data.duplicate ? "You are already registered" : "Registered successfully");
      setRegistered(true);
    } catch(e){ toast.error(e.response?.data?.detail || "Failed"); }
    finally { setRegistering(false); }
  };
  if (!h) return <div className="min-h-screen"><PublicHeader/><div className="p-10 text-slate-500">Loading...</div></div>;
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader/>
      <section className="relative h-72 md:h-96 overflow-hidden">
        <img alt={h.name} src={h.banner_url || "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1600&q=85"} className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/60 to-transparent"/>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-10 text-white">
          <Badge className="bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30 hover:bg-[#F59E0B]/20 w-fit">{h.theme}</Badge>
          <h1 className="font-display text-4xl md:text-6xl font-bold mt-3">{h.name}</h1>
          {h.tagline && <p className="mt-2 text-slate-200 text-lg">{h.tagline}</p>}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <h2 className="font-display text-xl font-semibold text-[#0A2540]">About</h2>
            <p className="mt-3 text-slate-700 leading-relaxed">{h.full_description || h.short_description}</p>
          </div>
          {h.eligibility && <div>
            <h2 className="font-display text-xl font-semibold text-[#0A2540]">Eligibility</h2>
            <p className="mt-3 text-slate-700">{h.eligibility}</p>
          </div>}
          {h.technologies?.length > 0 && <div>
            <h2 className="font-display text-xl font-semibold text-[#0A2540]">Technologies</h2>
            <div className="mt-3 flex flex-wrap gap-2">{h.technologies.map(t=><Badge key={t} className="bg-blue-100 text-blue-800 border-none hover:bg-blue-100">{t}</Badge>)}</div>
          </div>}
          {h.prizes?.length > 0 && <div>
            <h2 className="font-display text-xl font-semibold text-[#0A2540]">Prizes</h2>
            <ul className="mt-3 space-y-2">{h.prizes.map((p,i)=><li key={i} className="flex gap-2 text-slate-700">• {p}</li>)}</ul>
          </div>}
          {h.rules?.length > 0 && <div>
            <h2 className="font-display text-xl font-semibold text-[#0A2540]">Rules</h2>
            <ul className="mt-3 space-y-2">{h.rules.map((r,i)=><li key={i} className="flex gap-2 text-slate-700">• {r}</li>)}</ul>
          </div>}
          {h.timeline?.length > 0 && <div>
            <h2 className="font-display text-xl font-semibold text-[#0A2540]">Timeline</h2>
            <div className="mt-3 space-y-2">{h.timeline.map((t,i)=><div key={i} className="flex justify-between border-b border-slate-100 py-2 text-sm"><span className="text-slate-700">{t.label}</span><span className="text-slate-500">{t.date}</span></div>)}</div>
          </div>}
          {h.faqs?.length > 0 && <div>
            <h2 className="font-display text-xl font-semibold text-[#0A2540]">FAQs</h2>
            <div className="mt-3 space-y-3">{h.faqs.map((f,i)=><div key={i}><div className="font-semibold text-[#0A2540]">{f.q}</div><div className="text-slate-600 text-sm">{f.a}</div></div>)}</div>
          </div>}
        </div>
        <aside>
          <Card className="p-6 sticky top-24 border-slate-200">
            <div className="text-sm text-slate-500">Hackathon ID</div>
            <div className="font-mono text-xs text-[#0A2540] mt-1">{h.id}</div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Mode</span><span className="font-semibold text-[#0A2540]">{h.mode}</span></div>
              {h.location && <div className="flex justify-between"><span className="text-slate-500">Location</span><span className="font-semibold text-[#0A2540]">{h.location}</span></div>}
              {h.event_start && <div className="flex justify-between"><span className="text-slate-500">Starts</span><span className="font-semibold text-[#0A2540]">{h.event_start}</span></div>}
              {h.registration_end && <div className="flex justify-between"><span className="text-slate-500">Reg. Closes</span><span className="font-semibold text-[#0A2540]">{h.registration_end}</span></div>}
              <div className="flex justify-between"><span className="text-slate-500">Team size</span><span className="font-semibold text-[#0A2540]">{h.team_size_min}-{h.team_size_max}</span></div>
            </div>
            <Button onClick={register} disabled={registering || registered} className="w-full mt-6 bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="hack-register-btn">
              {registered ? "Registered" : registering ? "Registering..." : "Register Now"} {!registered && <ArrowRight className="w-4 h-4 ml-2"/>}
            </Button>
            {h.contact_email && <p className="text-xs text-slate-500 mt-4">Contact: {h.contact_email}</p>}
          </Card>
        </aside>
      </section>
      <PublicFooter/>
    </div>
  );
}
